import React, { useId } from "react";

interface Props {
  height?: number;
}

// Picture of the VSTBalance BLE remote with its Start/Stop button highlighted (pulsing glow), shown
// in remote control mode while the app waits for the therapist to press Start.
const RemoteStartHint: React.FC<Props> = ({ height = 300 }) => {
  // Unique ids: the prompt and its persistent version can be on screen together
  const id = useId().replace(/:/g, "");
  const fade = `remote-fade-${id}`;
  const fadeMask = `remote-fade-mask-${id}`;
  const glow = `remote-glow-${id}`;
  const font = "Helvetica, Arial, sans-serif";

  return (
    <svg
      className="remote-start-hint"
      xmlns="http://www.w3.org/2000/svg"
      height={height}
      width={(height * 240) / 420}
      viewBox="0 0 240 420"
      role="img"
      aria-label="Press Start/Stop on the remote"
    >
      <defs>
        <linearGradient id={fade} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0.86" stopColor="#fff" stopOpacity="1" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <mask id={fadeMask}>
          <rect x="0" y="0" width="240" height="420" fill={`url(#${fade})`} />
        </mask>
        <filter id={glow} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="9" />
        </filter>
      </defs>

      {/* The remote, fading out below the brand name */}
      <g mask={`url(#${fadeMask})`}>
        <rect x="12" y="8" width="216" height="470" rx="38" fill="#F3F3F1" stroke="#CFCFCC" strokeWidth="2" />
        <g fill="none" stroke="#B9B9B6" strokeWidth="4" strokeLinecap="round">
          <path d="M100 30 Q120 22 140 30" />
          <path d="M104 40 Q120 33 136 40" />
          <path d="M108 50 Q120 44 132 50" />
        </g>
        <rect x="113" y="66" width="14" height="296" rx="7" fill="#E7E7E4" stroke="#C7C7C4" strokeWidth="1.5" />
        <g stroke="#C4C4C1" strokeWidth="1.5" fill="#F7F7F5">
          <rect x="132" y="78" width="80" height="88" rx="8" />
          <rect x="28" y="174" width="80" height="88" rx="8" />
          <rect x="132" y="174" width="80" height="88" rx="8" />
          <rect x="28" y="270" width="80" height="88" rx="8" />
          <rect x="132" y="270" width="80" height="88" rx="8" />
        </g>
        <g fontFamily={font} fill="#8E8E8B" textAnchor="middle">
          <text x="68" y="227" fontSize="18">Rewind</text>
          <text x="68" y="321" fontSize="18">Next</text>
          <text x="172" y="227" fontSize="20">OK</text>
        </g>
        <g fill="none" stroke="#8E8E8B" strokeWidth="2.5" strokeLinejoin="round">
          <path d="M172 104 L188 134 L156 134 Z" />
          <path d="M172 330 L188 300 L156 300 Z" />
        </g>
        <text x="120" y="400" fontFamily={font} fontSize="24" fontWeight="700" fill="#B5B5B2" textAnchor="middle">
          VSTBalance
        </text>
      </g>

      {/* Start/Stop, highlighted */}
      <rect className="remote-start-hint-glow" x="24" y="74" width="88" height="96" rx="12" fill="#B14FC5" filter={`url(#${glow})`} />
      <rect x="28" y="78" width="80" height="88" rx="8" fill="#802392" />
      <g fontFamily={font} fontSize="19" fontWeight="700" fill="#FFFFFF" textAnchor="middle">
        <text x="68" y="114">Start</text>
        <line x1="46" y1="122" x2="90" y2="122" stroke="#FFFFFF" strokeWidth="1.5" />
        <text x="68" y="144">Stop</text>
      </g>
    </svg>
  );
};

export default RemoteStartHint;
